import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { Link } from '@/components/link/link';
import { LoginSignupButton } from '@/components/login-signup-button/login-signup-button';
import { useVideoAskModal } from '@/components/video-ask/lib/video-ask-client';
import { config } from '@/config/environment';
import { YellowButton } from '@/components/button/button';
import DeskTopView from './components/desktop-view';
import MobileView from './components/mobile-view';
import { processes } from './constant';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';

const MainDiv = styled.div`
  width: 100%;
  height: auto;
  margin: 0;
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: space-around;
  overflow: hidden;
  @media (max-width: 768px) {
    width: 100%;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  position: relative;
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;
const Row = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 12% 0 0 12.5%;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const ButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: 60px;
  @media (max-width: 768px) {
    justify-content: center;
    padding-bottom: 60px;
  }
`;
const ThirdDiv = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  margin: 0;
  flex-direction: column;
  justify-content: space-around;
  @media (max-width: 768px) {
    background: ${({ theme }) => theme.palette.common.midnight};
    margin-top: 50px;
  }
  ${ButtonDiv} {
    justify-content: center;
    margin-bottom: 60px;
  }
`;
const LeftVector = styled.img`
  left: 0;
  position: absolute;
  width: 42.01%;
  height: auto;
  max-width: 900px;
`;
const IntroDiv = styled(Grid)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 800px;
  @media (max-width: 768px) {
    padding: 0 20px;
    min-height: auto;
  }
`;
const PersonImage = styled(Image)`
  height: auto;
  width: 79.58%;
  margin-top: 30px;
  margin-left: 80px;
`;
PersonImage.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const Momenteer = styled(Image)`
  width: 100%;
  height: auto;
`;
Momenteer.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  text-align: start;
  z-index: 1;
  @media (max-width: 768px) {
    width: 80%;
    margin-top: -32px;
  }
`;

const Text = styled(Typography)`
  margin-top: 20px;
  margin-bottom: 32px;
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  z-index: 1;
  padding: 0 12.5% 0 0;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const BottomTitle = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 127.3%;
  margin-top: 120px;
  margin-bottom: 60px;
  color: #ffffff;
  text-align: center;
  @media (max-width: 768px) {
    text-align: start;
    padding: 20px;
    margin-bottom: 0;
    margin-top: 32px;
  }
`;
const ProcessRow = styled(Grid)`
  width: auto;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 60px;
`;
const ProcessColumn = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 32px;
`;
const Step = styled(Grid)`
  height: 103px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  ${Text} {
    text-align: start;
    font-weight: 600;
    font-size: 18px;
    margin-left: 20px;
    width: 70%;
  }
`;
const ProcessCard = styled(Grid)`
  height: 300px;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 5px;
  ${Text} {
    text-align: center;
  }
`;
const ImageDiv = styled.div`
  height: 153px;
  width: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const IconDiv = styled.div`
  height: 103px;
  width: 99px;
`;
const Icon = styled.img`
  height: auto;
  width: 148px;
  object-fit: cover;
  @media (max-width: 768px) {
    width: 99px;
    height: auto;
  }
`;
const Wave = styled.div`
  display: inline-block;
  position: absolute;
  width: 100%;
  z-index: -1;
  overflow: visible;
  margin-top: 16px;
  svg {
    display: inline-block;
    position: absolute;
    left: 0;
  }
`;
const BottomWave = styled(Wave)`
  position: relative;
  height: 100px;
  margin-top: -150px;
`;
const Dots = styled.img`
  margin: 72px 5px;
  height: 5px;
  width: 42px;
`;
const VerticalDots = styled.img`
  margin: 5px 70px;
  height: 28px;
  width: 5px;
`;
const StyledLink = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const Hosting = () => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const archiveMode = config.platformMode === 'archive';
  const [openCuratorVideoAsk] = useVideoAskModal(config.videoask.curator);
  const title = archiveMode ? 'Hosting with Curiko' : 'Start Hosting!';
  const introText = archiveMode
    ? 'Community members hosted experiences to share what they loved, gain new perspectives, and grow their networks. Through Curiko, they contributed their passions and connected with others on equal ground. Together, they explored what it could look like to build two-way relationships and more inclusive communities.'
    : `Share what you love, gain perspective, and grow your network! As a Host, Curiko is a platform to contribute your passions and connect with other humans, on equal ground. Together, we're learning how to build two-way relationships and truly inclusive communities.`;

  return (
    <MainDiv>
      <Div>
        <Wave>
          <svg
            width="100%"
            height="820"
            viewBox="0 0 1438 820"
            preserveAspectRatio={isMobile ? 'xMinYMin slice' : 'none'}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M543.315 774.03C374.436 785.663 196.634 850.308 0 802.76V0H1444.5V763.016C1444.5 763.016 1576.19 748.289 1269.35 802.76C1087.89 834.975 719.962 761.862 543.315 774.03Z"
              fill="#231727"
            />
          </svg>
        </Wave>
        {!isMobile && <LeftVector src="/discover/hosting/vector3.png" alt={'vector'} />}
        <Row container>
          {isMobile && (
            <IntroDiv item sm={12} xs={12}>
              <Momenteer src="/discover/hosting/momenteer.png" alt={'momenteer'} />
            </IntroDiv>
          )}
          <IntroDiv item md={6} sm={12} xs={12}>
            <Title>{title}</Title>
          </IntroDiv>
          <IntroDiv item md={6}>
            <Text>{introText}</Text>
            {!archiveMode && (
              <ButtonDiv>
                {/* <YellowButton label="Chat with a curator!" onClick={openCuratorVideoAsk} /> */}
                <LoginSignupButton label={'Create your experience'} type={'create'}>
                  <StyledLink href={'/my-experiences/create'}>
                    <YellowButton label="Create your experience" />
                  </StyledLink>
                </LoginSignupButton>
              </ButtonDiv>
            )}
            {!isMobile && <PersonImage src="/discover/hosting/momenteer1.png" alt={'image'} />}
          </IntroDiv>
        </Row>
      </Div>
      {isMobile ? <MobileView /> : <DeskTopView />}
      <ThirdDiv container>
        <BottomWave>
          <svg
            width="100%"
            height="808"
            viewBox="0 0 1440 808"
            preserveAspectRatio={isMobile ? 'xMinYMin slice' : 'none'}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M709.571 61.274C1089 -42.5698 1321.46 8.32119 1445 61.2744V806.5H-14V119C297.816 119 401.072 145.705 709.571 61.274Z"
              fill="#231727"
            />
          </svg>
        </BottomWave>
        <BottomTitle>{archiveMode ? 'How Hosting worked' : `What's the process?`}</BottomTitle>
        {isMobile ? (
          <ProcessColumn>
            <Step>
              <IconDiv>
                <Icon src={'/discover/hosting/step1.png'} alt={'icon'} />
              </IconDiv>
              <Text>{processes[0].name}</Text>
            </Step>
            <VerticalDots src={'/discover/hosting/mobile/dots.png'} alt={'dots'} />
            <Step>
              <IconDiv>
                <Icon src={'/discover/hosting/step2.png'} alt={'icon'} />
              </IconDiv>
              <Text>{processes[1].name}</Text>
            </Step>
            <VerticalDots src={'/discover/hosting/mobile/dots.png'} alt={'dots'} />
            <Step>
              <IconDiv>
                <Icon src={'/discover/hosting/step3.png'} alt={'icon'} />
              </IconDiv>
              <Text>{processes[2].name}</Text>
            </Step>
            <VerticalDots src={'/discover/hosting/mobile/dots.png'} alt={'dots'} />
            <Step>
              <IconDiv>
                <Icon src={'/discover/hosting/step4.png'} alt={'icon'} />
              </IconDiv>
              <Text>{processes[3].name}</Text>
            </Step>
          </ProcessColumn>
        ) : (
          <ProcessRow>
            {(processes || []).map((process, index) => (
              <React.Fragment key={process.name}>
                <ProcessCard>
                  <ImageDiv>
                    <Icon src={process.img} />
                  </ImageDiv>
                  <Text>{process.name}</Text>
                </ProcessCard>
                {index != processes.length - 1 ? (
                  <Dots src="/discover/experiences/dots.png" alt="dots" />
                ) : null}
              </React.Fragment>
            ))}
          </ProcessRow>
        )}
        {!archiveMode && (
          <ButtonDiv>
            <YellowButton label="Chat with curator!" onClick={openCuratorVideoAsk} />
          </ButtonDiv>
        )}
      </ThirdDiv>
    </MainDiv>
  );
};

export default Hosting;
